import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import OutsourcePOForm, { type OutsourcePOFormData } from "./OutsourcePOForm";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";

export default function OutsourceOrderCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const search = (useSearch({ strict: false }) || {}) as Record<string, string>;
  const prefillOrderId = search.orderId || "";
  const prefillVendorId = search.vendorId || "";
  const prefillDetailIdsStr = search.detailIds || "";
  // Mở từ Lệnh sản xuất (Đặt gia công): gia công một phần các công đoạn của lệnh
  const prefillProductionOrderId = search.productionOrderId || "";
  const prefillModel = search.model || (prefillProductionOrderId ? "HYBRID_FINISH_EXTERNAL" : "");
  const prefillLsxCode = search.lsxCode || "";
  const prefillDetailIds = prefillDetailIdsStr
    ? prefillDetailIdsStr.split(",")
    : [];

  const handleCreate = async (data: OutsourcePOFormData) => {
    try {
      const { items, filterDetailIds, ...header } = data;

      const totalQty = items.reduce((s, it) => s + (it.quantity || 0), 0);
      // Backend tự tính totalAmount
      // Nhiều dòng cùng quy cách: chỉ ghi một lần, không lặp lại (FB-O9IDAX)
      const uniq = (vals: (string | undefined | null)[]) =>
        Array.from(new Set(vals.map((v) => (v || "").trim()).filter(Boolean)));
      const productNames = uniq(items.map((it) => it.productName)).join(", ");
      const productSpecs = uniq(items.map((it) => it.productSpecs)).join("; ");

      const created = await trpcClient.outsourcePos.create.mutate({
        orderId: header.orderId || undefined,
        productionOrderId: header.productionOrderId || undefined,
        routingStepIds: header.routingStepIds || [],
        vendorId: header.vendorId,
        deploymentModel: header.deploymentModel || "OUTSOURCE_100",
        productName: productNames,
        productSpecs: productSpecs,
        notes: header.notes?.trim() || null,
        qtyOrdered: totalQty,
        unitPrice: items.length === 1 ? items[0].unitPrice : 0,
        issueDate: header.issueDate,
        expectedDate: header.expectedDate,
        status: header.status || "DRAFT",
        saveMode: (header as any).saveMode,
        materialIssued: items.map((it) => ({
          productId: it.productId,
          orderDetailId: it.orderDetailId || undefined,
          productName: it.productName,
          productSpecs: it.productSpecs,
          quantity: it.quantity,
          unit: it.unit || undefined,
          unitPrice: it.unitPrice,
          amount: it.amount,
          notes: it.notes,
        })),
      });
      toast.success("Tạo Đơn Gia công thành công!");
      await queryClient.invalidateQueries({ queryKey: ["outsource-pos"] });
      if ((created as any)?.id) {
        navigate({
          to: "/outsource-orders/$orderId",
          params: { orderId: (created as any).id },
          replace: true,
        });
      } else {
        navigate({ to: "/outsource-orders", replace: true });
      }
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi tạo đơn gia công");
    }
  };

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Đơn Gia công", to: "/outsource-orders" },
        { label: "Tạo mới" },
      ]}
      title="Tạo Đơn Gia Công"
    >
      <div className="bg-transparent border-0 p-0 shadow-none">
        <OutsourcePOForm
          defaultValues={{
            orderId: prefillOrderId,
            vendorId: prefillVendorId,
            filterDetailIds: prefillDetailIds,
            ...(prefillProductionOrderId
              ? { productionOrderId: prefillProductionOrderId, productionOrder: { lsxId: prefillLsxCode || prefillProductionOrderId } }
              : {}),
            ...(prefillModel ? { deploymentModel: prefillModel } : {}),
          }}
          onCancel={() => navigate({ to: "/outsource-orders" })}
          onSubmit={handleCreate}
        />
      </div>
    </StandardDetailLayout>
  );
}
